/**
 * CalibrationCheckPanel - shows the ASTM C1074 9.2 calibration check in the
 * pour wizard's maturity panel: whether the pour computes maturity on the
 * same basis (function, datum temperature, activation energy, specified
 * temperature) as its calibration was developed with.
 *
 *   match    small green status line
 *   differs  amber alert listing the differences; with `onAcknowledgeChange`
 *            a required confirmation checkbox
 *   mismatch red alert; the page disables saving
 *   unknown  grey information line
 *   none     information line (no calibration: maturity only)
 *   checkedOnSave  grey line: only the save can check this pour's inputs
 *   informational  differs / mismatch as a blue information alert, with no
 *            confirmation: the pour does not compute maturity, so the rule
 *            does not hold its save
 *
 * Without `onAcknowledgeChange` the panel is read-only (a saved pour's check).
 */
import React from 'react';
import { Alert, Box, Checkbox, CircularProgress, FormControlLabel, Typography } from '@mui/material';
import {
  CheckCircleOutline as CheckCircleOutlineIcon,
  InfoOutlined as InfoOutlinedIcon,
} from '@mui/icons-material';
import type { CalibrationCheck } from '../../services/pours';
import {
  ACKNOWLEDGE_DIFFERENCE_LABEL,
  CHECKED_ON_SAVE_TEXT,
  INFORMATION_ONLY_TEXT,
  MISMATCH_ADVICE_TEXT,
  basisDifferenceLines,
} from '../../utils/calibrationCheck';

export interface CalibrationCheckPanelProps {
  check: CalibrationCheck | null | undefined;
  loading?: boolean;
  /** The check request failed (the server still checks on save). */
  failed?: boolean;
  /** A planned pour that gets no calibration: why (noCalibrationText). */
  noCalibrationText?: string | null;
  /** The check endpoint cannot see what the planned pour computes with
   *  (only the save can check it): says so instead of showing its result. */
  checkedOnSave?: boolean;
  /** The pour does not compute maturity: a difference or mismatch is shown
   *  for information, never as a confirmation or a held save. */
  informational?: boolean;
  acknowledged?: boolean;
  /** Given for a planned pour: shows the 'differs' confirmation checkbox. */
  onAcknowledgeChange?: (value: boolean) => void;
  /** What the user can do about a 'mismatch' on a planned pour (default:
   *  choose another mix design). */
  mismatchAdvice?: string;
}

const Line: React.FC<{
  status: string;
  color: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}> = ({ status, color, icon, children }) => (
  <Box
    role="status"
    data-testid="calibration-check"
    data-status={status}
    sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, color }}
  >
    <Box sx={{ display: 'flex', pt: '2px', flexShrink: 0 }}>{icon}</Box>
    <Typography variant="body2" sx={{ color: 'inherit' }}>
      {children}
    </Typography>
  </Box>
);

const DifferenceList: React.FC<{ check: CalibrationCheck }> = ({ check }) => {
  const lines = basisDifferenceLines(check);
  if (lines.length === 0) return null;
  return (
    <Box component="ul" sx={{ m: 0, mt: 0.75, pl: 2.5 }}>
      {lines.map((line) => (
        <Typography component="li" variant="body2" key={line}>
          {line}
        </Typography>
      ))}
    </Box>
  );
};

const CalibrationCheckPanel: React.FC<CalibrationCheckPanelProps> = ({
  check,
  loading = false,
  failed = false,
  noCalibrationText = null,
  checkedOnSave = false,
  informational = false,
  acknowledged = false,
  onAcknowledgeChange,
  mismatchAdvice = MISMATCH_ADVICE_TEXT,
}) => {
  const planned = Boolean(onAcknowledgeChange);

  if (noCalibrationText && (!check || check.status === 'none')) {
    return (
      <Line status="none" color="info.main" icon={<InfoOutlinedIcon fontSize="small" />}>
        {noCalibrationText}
      </Line>
    );
  }
  if (checkedOnSave) {
    return (
      <Line status="on-save" color="text.secondary" icon={<InfoOutlinedIcon fontSize="small" />}>
        {CHECKED_ON_SAVE_TEXT}
      </Line>
    );
  }
  if (loading) {
    return (
      <Line status="loading" color="text.secondary" icon={<CircularProgress size={14} color="inherit" />}>
        Checking the maturity basis against the calibration...
      </Line>
    );
  }
  if (!check) {
    if (!failed) return null;
    return (
      <Line status="failed" color="text.secondary" icon={<InfoOutlinedIcon fontSize="small" />}>
        The maturity basis could not be checked against the calibration. It is checked again on save.
      </Line>
    );
  }

  if (informational && (check.status === 'differs' || check.status === 'mismatch')) {
    return (
      <Alert severity="info" data-testid="calibration-check" data-status={check.status}>
        {check.text}
        <DifferenceList check={check} />
        <Typography variant="body2" sx={{ mt: 0.75, fontWeight: 500 }}>
          {INFORMATION_ONLY_TEXT}
        </Typography>
      </Alert>
    );
  }

  switch (check.status) {
    case 'match':
      return (
        <Line status="match" color="success.main" icon={<CheckCircleOutlineIcon fontSize="small" />}>
          {check.text}
        </Line>
      );
    case 'differs':
      return (
        <Alert severity="warning" data-testid="calibration-check" data-status="differs">
          {check.text}
          <DifferenceList check={check} />
          {planned && (
            <FormControlLabel
              sx={{ mt: 0.5, alignItems: 'flex-start' }}
              control={
                <Checkbox
                  size="small"
                  required
                  checked={acknowledged}
                  onChange={(e) => onAcknowledgeChange?.(e.target.checked)}
                  sx={{ py: 0.25 }}
                />
              }
              label={<Typography variant="body2">{ACKNOWLEDGE_DIFFERENCE_LABEL}</Typography>}
            />
          )}
        </Alert>
      );
    case 'mismatch':
      return (
        <Alert severity="error" data-testid="calibration-check" data-status="mismatch">
          {check.text}
          <DifferenceList check={check} />
          {planned && (
            <Typography variant="body2" sx={{ mt: 0.75, fontWeight: 500 }}>
              {mismatchAdvice}
            </Typography>
          )}
        </Alert>
      );
    case 'unknown':
      return (
        <Line status="unknown" color="text.secondary" icon={<InfoOutlinedIcon fontSize="small" />}>
          {check.text}
        </Line>
      );
    case 'none':
    default:
      return (
        <Line status={check.status || 'none'} color="info.main" icon={<InfoOutlinedIcon fontSize="small" />}>
          {check.text}
        </Line>
      );
  }
};

export default CalibrationCheckPanel;
